import { IStringerRunSpan, IVec3 } from '@stair-craft/common/util';

/** A side profile in (run, y): the area between a top and a bottom polyline. */
export interface IProfilePoint {
    run: number;
    y: number;
}

export interface IKosourRunAxis {
    runAlongX: boolean;
    runOf: (p: IVec3) => number;
    fixedCoord: number;
    dRun: number;
}

/** Whether a stringer profile runs along X or Z, with the run and fixed-coordinate accessors. */
export function kosourRunAxis(points: readonly IVec3[]): IKosourRunAxis {
    const first: IVec3 = points[0];
    const last: IVec3 = points[points.length - 1];
    const runAlongX: boolean = Math.abs(last.x - first.x) > Math.abs(last.z - first.z);
    const runOf: (p: IVec3) => number = runAlongX ? (p: IVec3): number => p.x : (p: IVec3): number => p.z;
    const fixedCoord: number = runAlongX ? first.z : first.x;
    return { runAlongX, runOf, fixedCoord, dRun: runOf(last) - runOf(first) };
}

/** Keeps the part of a polygon at or above the floor (y ≥ 0). */
export function clipProfileAtFloor(polygon: readonly IProfilePoint[]): IProfilePoint[] {
    const result: IProfilePoint[] = [];
    for (let i: number = 0; i < polygon.length; i++) {
        const current: IProfilePoint = polygon[i];
        const next: IProfilePoint = polygon[(i + 1) % polygon.length];
        const keepsCurrent: boolean = current.y >= 0;
        const keepsNext: boolean = next.y >= 0;
        if (keepsCurrent) {
            result.push(current);
        }
        if (keepsCurrent !== keepsNext) {
            const t: number = current.y / (current.y - next.y);
            result.push({ run: current.run + t * (next.run - current.run), y: 0 });
        }
    }
    return result;
}

/** Keeps the part of a polygon on one side of a run line: at or past `bound` when `ahead`, at or before it otherwise. */
function clipProfileAtRun(polygon: readonly IProfilePoint[], bound: number, ahead: boolean): IProfilePoint[] {
    const side: (point: IProfilePoint) => number = (point: IProfilePoint): number => (ahead ? point.run - bound : bound - point.run);
    const result: IProfilePoint[] = [];
    for (let i: number = 0; i < polygon.length; i++) {
        const current: IProfilePoint = polygon[i];
        const next: IProfilePoint = polygon[(i + 1) % polygon.length];
        const keepsCurrent: boolean = side(current) >= 0;
        if (keepsCurrent) {
            result.push(current);
        }
        if (keepsCurrent !== side(next) >= 0) {
            const t: number = side(current) / (side(current) - side(next));
            result.push({ run: bound, y: current.y + t * (next.y - current.y) });
        }
    }
    return result;
}

/** Whether `point` repeats `previous` or turns the outline straight back along the line it came by. */
function isSpike(previous: IProfilePoint, point: IProfilePoint, next: IProfilePoint): boolean {
    const inRun: number = point.run - previous.run;
    const inY: number = point.y - previous.y;
    const outRun: number = next.run - point.run;
    const outY: number = next.y - point.y;
    const inLength: number = Math.hypot(inRun, inY);
    const outLength: number = Math.hypot(outRun, outY);
    if (inLength < 1e-6) {
        return true;
    }
    const collinear: boolean = Math.abs(inRun * outY - inY * outRun) <= 1e-9 * inLength * outLength;
    return collinear && inRun * outRun + inY * outY < 0;
}

/**
 * Drops repeated vertices and zero-width spikes from a closed outline. A spike — the outline
 * running up a line and straight back down it — encloses no area, but it leaves a vertex lying
 * on another edge, and the triangulator may then fill a triangle outside the outline: a kosour
 * whose last rise runs up to the landing and whose bottom edge comes back down the same line
 * grew a wedge over the treads of its flight.
 */
export function withoutSpikes(outline: readonly IProfilePoint[]): IProfilePoint[] {
    const result: IProfilePoint[] = [...outline];
    let removed: boolean = true;
    while (removed && result.length > 3) {
        removed = false;
        for (let i: number = 0; i < result.length; i++) {
            const previous: IProfilePoint = result[(i - 1 + result.length) % result.length];
            const next: IProfilePoint = result[(i + 1) % result.length];
            if (isSpike(previous, result[i], next)) {
                result.splice(i, 1);
                removed = true;
                break;
            }
        }
    }
    return result;
}

/** The closed profile of a тетива: top polyline forward, bottom polyline back, cut at the floor. */
export function tetivaProfile(top: readonly IVec3[], bottom: readonly IVec3[], runOf: (point: IVec3) => number): IProfilePoint[] {
    const outline: IProfilePoint[] = [
        ...top.map((point: IVec3) => ({ run: runOf(point), y: point.y })),
        ...[...bottom].reverse().map((point: IVec3) => ({ run: runOf(point), y: point.y })),
    ];
    return clipProfileAtFloor(outline);
}

/** The bottom edge offset square to the overall slope by `width`, from the last point back to the first. */
function perpendicularBottomEdge(first: IProfilePoint, last: IProfilePoint, width: number): IProfilePoint[] {
    const dRun: number = last.run - first.run;
    const dy: number = last.y - first.y;
    const slopeLen: number = Math.hypot(dRun, dy);
    const perpRun: number = slopeLen > 0 ? dy / slopeLen : 0;
    const perpY: number = slopeLen > 0 ? -dRun / slopeLen : -1;
    return [
        { run: last.run + perpRun * width, y: last.y + perpY * width },
        { run: first.run + perpRun * width, y: first.y + perpY * width },
    ];
}

/** The inner notch corners of a sawtooth top: where the rise to the next tread starts up from a level tread seat. */
function notchCorners(top: readonly IProfilePoint[]): IProfilePoint[] {
    const isSeat: (from: IProfilePoint, to: IProfilePoint) => boolean = (from: IProfilePoint, to: IProfilePoint): boolean =>
        Math.abs(from.y - to.y) < 1e-6 && Math.abs(from.run - to.run) > 1e-6;
    return top.filter(
        (point: IProfilePoint, index: number) =>
            index > 0 && index + 1 < top.length && isSeat(top[index - 1], point) && top[index + 1].y > point.y + 1e-6
    );
}

/** A straight bottom edge: the height `base` at run 0 and the slope `tan`. */
interface IEdgeLine {
    base: number;
    tan: number;
}

/**
 * Where an edge from `end` up to the bottom line leaves it, running past `corner` at `width`: the
 * tangent from `end` to the circle of radius `width` round the corner, on the bottom-line side.
 * `undefined` when `end` lies inside that circle — the edge then stays on the bottom line.
 */
function tangentKink(end: IProfilePoint, corner: IProfilePoint, width: number, line: IEdgeLine): IProfilePoint | undefined {
    const toCorner: IProfilePoint = { run: corner.run - end.run, y: corner.y - end.y };
    const distance: number = Math.hypot(toCorner.run, toCorner.y);
    if (distance <= width) {
        return undefined;
    }
    const spread: number = Math.asin(width / distance);
    const heading: number = Math.atan2(toCorner.y, toCorner.run);
    const kinks: IProfilePoint[] = [heading - spread, heading + spread].flatMap((angle: number): IProfilePoint[] => {
        const direction: IProfilePoint = { run: Math.cos(angle), y: Math.sin(angle) };
        const cross: number = direction.run * toCorner.y - direction.y * toCorner.run;
        const along: number = (line.base + line.tan * end.run - end.y) / (direction.y - line.tan * direction.run);
        // The corner lies above the tangent: the tangent passes under the notch, not through it.
        const below: boolean = direction.run > 0 ? cross > 0 : cross < 0;
        return below && Number.isFinite(along) && along > 0
            ? [{ run: end.run + along * direction.run, y: end.y + along * direction.y }]
            : [];
    });
    return kinks[0];
}

/**
 * The kosour bottom edge from the last top point back to the first. It runs parallel to the line
 * through the notch corners and `width` square below them (SC-CALC-095): a flight that meets a
 * landing has its end points off that line, and an edge at width/cos(stairAngle) below the end
 * points left the board thinner under the notches next to the landing. Past the outer notches the
 * edge rises back to that old height where it is higher, along a tangent that keeps `width` to the
 * outer notch, so the board end still meets the turn boards and clears the treads beside it. A
 * board with fewer than two notches keeps the old edge.
 */
function kosourBottomEdge(top: readonly IProfilePoint[], width: number, bottomSlope: { rise: number; run: number }): IProfilePoint[] {
    const first: IProfilePoint = top[0];
    const last: IProfilePoint = top[top.length - 1];
    const endOffset: number = width / Math.cos(Math.atan(bottomSlope.rise / Math.max(0.0001, bottomSlope.run)));
    const endFirst: IProfilePoint = { run: first.run, y: first.y - endOffset };
    const endLast: IProfilePoint = { run: last.run, y: last.y - endOffset };
    const corners: IProfilePoint[] = notchCorners(top);
    if (corners.length < 2) {
        return [endLast, endFirst];
    }
    const low: IProfilePoint = corners[0];
    const high: IProfilePoint = corners[corners.length - 1];
    const tan: number = (high.y - low.y) / (high.run - low.run);
    const lineAt: (run: number) => number = (run: number): number => low.y + (run - low.run) * tan;
    const offset: number =
        width / Math.cos(Math.atan(tan)) + Math.max(...corners.map((point: IProfilePoint) => lineAt(point.run) - point.y));
    const line: IEdgeLine = { base: low.y - low.run * tan - offset, tan };
    const edgeAt: (run: number) => IProfilePoint = (run: number): IProfilePoint => ({ run, y: line.base + run * tan });
    const sideOf: (end: IProfilePoint, corner: IProfilePoint) => IProfilePoint[] = (end: IProfilePoint, corner: IProfilePoint) => {
        const kink: IProfilePoint | undefined = end.y > edgeAt(end.run).y ? tangentKink(end, corner, width, line) : undefined;
        return kink ? [end, kink] : [edgeAt(end.run)];
    };
    return [...sideOf(endLast, high), ...sideOf(endFirst, low).reverse()];
}

/**
 * The closed side profile of a kosour in (run, y): the sawtooth top from the step profile, then
 * the bottom edge back — under the notches when `bottomSlope` is given and cut at the floor so the
 * board never pokes below the entry side, square to the slope otherwise. Repeated vertices and
 * spikes are dropped.
 */
export function kosourProfile(
    points: readonly IVec3[],
    width: number,
    bottomSlope: { rise: number; run: number } | undefined,
    runSpan?: IStringerRunSpan
): IProfilePoint[] {
    const { runOf } = kosourRunAxis(points);
    const top: IProfilePoint[] = points.map((point: IVec3) => ({ run: runOf(point), y: point.y }));
    const first: IProfilePoint = top[0];
    const last: IProfilePoint = top[top.length - 1];
    let outline: IProfilePoint[] = bottomSlope
        ? clipProfileAtFloor([...top, ...kosourBottomEdge(top, width, bottomSlope)])
        : [...top, ...perpendicularBottomEdge(first, last, width)];
    // A board ending in a post's groove is cut square at that run (SC-CALC-094).
    if (runSpan?.from !== undefined) {
        outline = clipProfileAtRun(outline, runSpan.from, true);
    }
    if (runSpan?.to !== undefined) {
        outline = clipProfileAtRun(outline, runSpan.to, false);
    }
    return withoutSpikes(outline);
}
